import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { SpecraButton, SpecraCard, SpecraCardContent, SpecraCheckboxField, SpecraCombobox, SpecraHeading, SpecraKeyValueRow, SpecraText, SpecraTextarea } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { useEffect, useState } from 'react';
import { useApproveLaborCategoryMapping, useCorrectLaborCategoryMapping, useProposeLaborCategoryMapping, useRejectLaborCategoryMapping, useWithdrawLaborCategoryMapping } from './hooks';
import { MAPPING_TYPE_LABELS, mappingStatement } from './mappingDirection';
import { affectedStaffingReferences, hasReviewEndpoints, parseMappingBasis, parseMappingJson } from './mappingReview';
const DEFINITION_FIELDS = new Set([
    'id',
    'title',
    'description',
    'version',
    'source',
    'source_version',
    'company_profile_id',
    'company_name',
    'contract_vehicle_id',
    'contract_vehicle_name',
    'solicitation_ref',
    'qualifications',
    'clearance',
    'certifications',
    'status',
    'soc_code',
    'family',
    'specialty',
    'seniority'
]);
function display(value) {
    if (value === undefined || value === null || value === '') return 'Not recorded';
    if (Array.isArray(value)) return value.map(display).join('; ');
    if (typeof value === 'object') return Object.entries(value).map(([key, v])=>`${key.replace(/_/g, ' ')}: ${display(v)}`).join('; ');
    return String(value);
}
function Definition({ title, definition }) {
    return /*#__PURE__*/ _jsx(SpecraCard, {
        children: /*#__PURE__*/ _jsxs(SpecraCardContent, {
            children: [
                /*#__PURE__*/ _jsx(SpecraHeading, {
                    children: title
                }),
                definition ? Object.entries(definition).filter(([key])=>DEFINITION_FIELDS.has(key)).map(([key, value])=>/*#__PURE__*/ _jsx(SpecraKeyValueRow, {
                        label: key.replace(/_/g, ' '),
                        value: display(value)
                    }, key)) : /*#__PURE__*/ _jsx(SpecraText, {
                    children: "Definition snapshot unavailable"
                })
            ]
        })
    });
}
function Basis({ title, basis }) {
    return /*#__PURE__*/ _jsxs("section", {
        "aria-label": title,
        children: [
            /*#__PURE__*/ _jsx(SpecraHeading, {
                children: title
            }),
            basis ? /*#__PURE__*/ _jsxs(_Fragment, {
                children: [
                    basis.provenance && /*#__PURE__*/ _jsx(SpecraText, {
                        children: basis.provenance
                    }),
                    /*#__PURE__*/ _jsxs(SpecraText, {
                        children: [
                            "Relation:",
                            ' ',
                            MAPPING_TYPE_LABELS[basis.mapping_type] ?? (basis.mapping_type || 'Not recorded')
                        ]
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: "grid grid-cols-1 gap-3 sm:grid-cols-2",
                        children: [
                            /*#__PURE__*/ _jsx(Definition, {
                                title: "Source definition",
                                definition: basis.source
                            }),
                            /*#__PURE__*/ _jsx(Definition, {
                                title: basis.anchor ? 'Occupational anchor' : 'Target definition',
                                definition: basis.target ?? basis.anchor
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsx(SpecraKeyValueRow, {
                        label: "Suggestion evidence / rationale",
                        value: display(basis.evidence)
                    }),
                    /*#__PURE__*/ _jsx(SpecraKeyValueRow, {
                        label: "Suggestion confidence",
                        value: display(basis.confidence)
                    }),
                    /*#__PURE__*/ _jsx(SpecraKeyValueRow, {
                        label: "Proposed by",
                        value: basis.proposed_by || 'Not recorded'
                    })
                ]
            }) : /*#__PURE__*/ _jsx(SpecraText, {
                children: "Basis unavailable. Refresh before reviewing; approval requires exact definition snapshots."
            })
        ]
    });
}
export function MappingReviewPanel({ companyId, initialMapping, entries, anchors, history, canEdit, onOpenSource, onRefresh, onClose }) {
    const [mapping, setMapping] = useState(initialMapping);
    const targetOf = (m)=>m.toEntryId ? `entry:${m.toEntryId}` : `anchor:${m.toAnchorId}`;
    const [target, setTarget] = useState(targetOf(mapping));
    const [relation, setRelation] = useState(mapping.mappingType);
    const [reason, setReason] = useState('');
    const [inspected, setInspected] = useState(false);
    const [mustRefresh, setMustRefresh] = useState(false);
    const [error, setError] = useState('');
    const [refreshing, setRefreshing] = useState(false);
    useEffect(()=>{
        if (initialMapping.status === 'REVIEW_NEEDED') {
            setMapping(initialMapping);
            setInspected(false);
        }
    }, [
        initialMapping
    ]);
    const approve = useApproveLaborCategoryMapping();
    const reject = useRejectLaborCategoryMapping();
    const correct = useCorrectLaborCategoryMapping();
    const withdraw = useWithdrawLaborCategoryMapping();
    const propose = useProposeLaborCategoryMapping();
    const [carryResult, setCarryResult] = useState('');
    const currentSource = entries.find((e)=>e.id === mapping.currentFromEntryId);
    const currentTarget = entries.find((e)=>e.id === mapping.currentToEntryId);
    const currentAnchor = anchors.find((a)=>a.id === mapping.toAnchorId);
    const busy = refreshing || approve.isPending || reject.isPending || correct.isPending || withdraw.isPending || propose.isPending;
    const dirty = target !== targetOf(mapping) || relation !== mapping.mappingType;
    const reviewed = parseMappingBasis(mapping.reviewedBasisJson);
    const pending = mapping.status === 'PENDING';
    const update = (next)=>{
        if (!next) throw new Error('Mapping response unavailable. Refresh and inspect again.');
        setMapping(next);
        setTarget(targetOf(next));
        setRelation(next.mappingType);
        setReason('');
        setInspected(false);
    };
    const perform = async (action)=>{
        setError('');
        try {
            await action();
        } catch (err) {
            setInspected(false);
            setMustRefresh(true);
            setError(`${err instanceof Error ? err.message : 'Mapping update failed'}. Refresh and inspect the current review before continuing.`);
        }
    };
    return /*#__PURE__*/ _jsx(SpecraCard, {
        "data-testid": Testids.deliveryRegistries.lcatMappingReview,
        children: /*#__PURE__*/ _jsxs(SpecraCardContent, {
            className: "space-y-4",
            children: [
                /*#__PURE__*/ _jsxs(SpecraHeading, {
                    children: [
                        "Mapping review · ",
                        mapping.status,
                        " · revision ",
                        String(mapping.reviewRevision ?? BigInt(0))
                    ]
                }),
                /*#__PURE__*/ _jsx(SpecraText, {
                    as: "p",
                    children: mappingStatement(mapping)
                }),
                /*#__PURE__*/ _jsx(SpecraText, {
                    as: "p",
                    children: "Mappings do not establish contractual substitutability or equal rates. Sharing an anchor does not approve a direct pair."
                }),
                /*#__PURE__*/ _jsx(SpecraButton, {
                    variant: "ghost",
                    onClick: ()=>onOpenSource(mapping.currentFromEntryId || mapping.fromEntryId),
                    "data-testid": Testids.deliveryRegistries.lcatMappingSource,
                    children: "Open source category (review retained)"
                }),
                /*#__PURE__*/ _jsx(Basis, {
                    title: "Original proposal basis",
                    basis: parseMappingBasis(mapping.originalBasisJson)
                }),
                /*#__PURE__*/ _jsx("div", {
                    "data-testid": Testids.deliveryRegistries.lcatMappingReviewedBasis,
                    children: /*#__PURE__*/ _jsx(Basis, {
                        title: "Reviewed basis",
                        basis: reviewed
                    })
                }),
                /*#__PURE__*/ _jsx(SpecraKeyValueRow, {
                    label: "Correction reason / reviewer",
                    value: `${mapping.correctionReason || 'No correction'} · ${mapping.correctedBy || 'Not recorded'}`
                }),
                /*#__PURE__*/ _jsx(SpecraKeyValueRow, {
                    label: "Original decision",
                    value: `${mapping.decidedBy || 'Not decided'} · ${mapping.decidedAt?.toDate().toLocaleString() ?? 'No decision date'}`
                }),
                mapping.status === 'REVIEW_NEEDED' && /*#__PURE__*/ _jsxs("section", {
                    "aria-label": "Current endpoint comparison",
                    children: [
                        /*#__PURE__*/ _jsx(SpecraHeading, {
                            children: "Current endpoint comparison"
                        }),
                        /*#__PURE__*/ _jsx(SpecraText, {
                            children: "Review needed. The retained approval covers the reviewed definitions above, not these current endpoints. Carry forward creates a pending proposal for a new review."
                        }),
                        [
                            currentSource,
                            currentTarget
                        ].filter((e)=>!!e).map((e)=>/*#__PURE__*/ _jsxs(SpecraText, {
                                children: [
                                    e.title,
                                    " · v",
                                    e.version,
                                    " · ",
                                    e.id,
                                    " · ",
                                    e.description,
                                    " · ",
                                    e.qualifications,
                                    " ·",
                                    e.clearance,
                                    " · ",
                                    e.certifications,
                                    ' · ',
                                    e.companyName || e.companyProfileId || 'No company',
                                    " ·",
                                    e.contractVehicleName || e.contractVehicleId || 'No vehicle',
                                    " · ",
                                    e.source
                                ]
                            }, e.id)),
                        currentAnchor && /*#__PURE__*/ _jsxs(SpecraText, {
                            children: [
                                currentAnchor.socCode,
                                " · ",
                                currentAnchor.title,
                                " · ",
                                currentAnchor.sourceVersion,
                                " ·",
                                currentAnchor.description
                            ]
                        }),
                        canEdit && /*#__PURE__*/ _jsx(SpecraButton, {
                            disabled: busy || mustRefresh || !currentSource || !currentTarget && !currentAnchor,
                            onClick: ()=>void perform(async ()=>{
                                    const response = await propose.mutateAsync({
                                        companyId,
                                        fromEntryId: mapping.currentFromEntryId,
                                        toEntryId: mapping.currentToEntryId,
                                        toAnchorId: mapping.toAnchorId,
                                        mappingType: mapping.mappingType,
                                        evidenceJson: JSON.stringify({
                                            prior_mapping_id: mapping.id,
                                            rationale: 'Carry forward for explicit review of current definitions',
                                            prior_evidence: parseMappingJson(mapping.evidenceJson)
                                        })
                                    });
                                    setCarryResult(response.created ? 'New pending proposal created. Open it in Pending and inspect before approval.' : `Existing ${response.mapping?.status ?? ''} proposal retained. Open its review.`);
                                }),
                            children: "Carry mapping forward"
                        }),
                        carryResult && /*#__PURE__*/ _jsx("output", {
                            children: carryResult
                        })
                    ]
                }),
                mapping.status === 'REJECTED' && /*#__PURE__*/ _jsx(SpecraText, {
                    children: "This rejection is immutable. Open the source category to propose reconsideration with a materially changed target, relation or evidence."
                }),
                /*#__PURE__*/ _jsxs("section", {
                    "aria-label": "Earlier rejection history",
                    children: [
                        /*#__PURE__*/ _jsx(SpecraHeading, {
                            children: "Earlier rejection history"
                        }),
                        history.filter((m)=>m.id !== mapping.id && m.status === 'REJECTED' && (m.fromEntryId === mapping.fromEntryId || m.toEntryId === mapping.fromEntryId)).map((m)=>/*#__PURE__*/ _jsxs(SpecraText, {
                                children: [
                                    m.id,
                                    ": ",
                                    mappingStatement(m),
                                    " · ",
                                    m.decidedBy || 'Reviewer not recorded',
                                    " ·",
                                    ' ',
                                    m.decidedAt?.toDate().toLocaleString() ?? 'Date not recorded',
                                    " ·",
                                    ' ',
                                    display(parseMappingBasis(m.originalBasisJson)?.evidence ?? parseMappingJson(m.evidenceJson))
                                ]
                            }, m.id))
                    ]
                }),
                error && /*#__PURE__*/ _jsx(SpecraText, {
                    role: "alert",
                    children: error
                }),
                /*#__PURE__*/ _jsx(SpecraButton, {
                    variant: "secondary",
                    disabled: busy,
                    onClick: ()=>void perform(async ()=>{
                            setRefreshing(true);
                            try {
                                update(await onRefresh());
                                setMustRefresh(false);
                            } finally{
                                setRefreshing(false);
                            }
                        }),
                    "data-testid": Testids.deliveryRegistries.lcatMappingRefresh,
                    children: "Refresh review"
                }),
                pending && canEdit && /*#__PURE__*/ _jsxs(_Fragment, {
                    children: [
                        /*#__PURE__*/ _jsx(SpecraCombobox, {
                            label: "Reviewed target",
                            disabled: busy || mustRefresh,
                            fallbackLabel: mapping.toEntryTitle || mapping.toAnchorTitle || target,
                            triggerProps: {
                                'data-testid': Testids.deliveryRegistries.lcatMappingTarget
                            },
                            value: target,
                            onValueChange: (v)=>{
                                setTarget(v ?? '');
                                setInspected(false);
                            },
                            options: [
                                ...entries.filter((e)=>e.id !== mapping.fromEntryId).map((e)=>({
                                        value: `entry:${e.id}`,
                                        label: `${e.title} · ${e.companyName || e.companyProfileId || 'No company'} · ${e.contractVehicleName || e.contractVehicleId || 'No vehicle'} · v${e.version} · ${e.id}`
                                    })),
                                ...anchors.map((a)=>({
                                        value: `anchor:${a.id}`,
                                        label: `${a.socCode} ${a.title} · ${a.id}`
                                    }))
                            ]
                        }),
                        /*#__PURE__*/ _jsx(SpecraCombobox, {
                            label: "Reviewed relation",
                            disabled: busy || mustRefresh,
                            triggerProps: {
                                'data-testid': Testids.deliveryRegistries.lcatMappingRelation
                            },
                            value: relation,
                            onValueChange: (v)=>{
                                setRelation(v ?? '');
                                setInspected(false);
                            },
                            options: Object.entries(MAPPING_TYPE_LABELS).map(([value, label])=>({
                                    value,
                                    label
                                }))
                        }),
                        /*#__PURE__*/ _jsx(SpecraTextarea, {
                            "aria-label": "Correction reason",
                            placeholder: "Reason for correction",
                            value: reason,
                            onChange: (e)=>setReason(e.target.value),
                            "data-testid": Testids.deliveryRegistries.lcatMappingCorrectionReason
                        }),
                        /*#__PURE__*/ _jsx(SpecraButton, {
                            disabled: busy || mustRefresh || !dirty || !reason.trim() || !target || !relation,
                            onClick: ()=>void perform(async ()=>{
                                    const response = await correct.mutateAsync({
                                        id: mapping.id,
                                        toEntryId: target.startsWith('entry:') ? target.slice(6) : '',
                                        toAnchorId: target.startsWith('anchor:') ? target.slice(7) : '',
                                        mappingType: relation,
                                        reason: reason.trim(),
                                        expectedRevision: mapping.reviewRevision
                                    });
                                    update(response.mapping);
                                }),
                            "data-testid": Testids.deliveryRegistries.lcatMappingCorrect,
                            children: "Save correction"
                        }),
                        /*#__PURE__*/ _jsx(SpecraText, {
                            children: "Save changes first, then inspect the returned definitions and version before approval."
                        }),
                        /*#__PURE__*/ _jsx(SpecraCheckboxField, {
                            label: "I inspected these exact reviewed definitions, versions and relation",
                            controlProps: {
                                checked: inspected,
                                disabled: busy || dirty || mustRefresh || !hasReviewEndpoints(reviewed),
                                onCheckedChange: (v)=>setInspected(v === true)
                            },
                            "data-testid": Testids.deliveryRegistries.lcatMappingInspected
                        }),
                        /*#__PURE__*/ _jsx(SpecraButton, {
                            disabled: busy || dirty || mustRefresh || !inspected || !hasReviewEndpoints(reviewed) || !(mapping.reviewRevision > BigInt(0)),
                            onClick: ()=>void perform(async ()=>{
                                    update((await approve.mutateAsync({
                                        id: mapping.id,
                                        reviewRevision: mapping.reviewRevision
                                    })).mapping);
                                }),
                            "data-testid": Testids.deliveryRegistries.lcatMappingApprove,
                            children: "Approve reviewed mapping"
                        }),
                        /*#__PURE__*/ _jsx(SpecraButton, {
                            variant: "destructive",
                            disabled: busy || mustRefresh,
                            onClick: ()=>void perform(async ()=>{
                                    update((await reject.mutateAsync(mapping.id)).mapping);
                                }),
                            "data-testid": Testids.deliveryRegistries.lcatMappingReject,
                            children: "Reject mapping"
                        })
                    ]
                }),
                mapping.status === 'APPROVED' && canEdit && /*#__PURE__*/ _jsxs(_Fragment, {
                    children: [
                        /*#__PURE__*/ _jsx(SpecraText, {
                            children: "Withdrawal removes this mapping from current use. Referenced staffing selections and rates stay unchanged; affected working content receives a review notice. Propose any replacement through normal pending review."
                        }),
                        /*#__PURE__*/ _jsx(SpecraTextarea, {
                            "aria-label": "Withdrawal reason",
                            value: reason,
                            onChange: (e)=>setReason(e.target.value),
                            "data-testid": Testids.deliveryRegistries.lcatMappingWithdrawalReason
                        }),
                        /*#__PURE__*/ _jsx(SpecraButton, {
                            variant: "destructive",
                            disabled: busy || mustRefresh || !reason.trim(),
                            onClick: ()=>void perform(async ()=>{
                                    update((await withdraw.mutateAsync({
                                        id: mapping.id,
                                        reason: reason.trim()
                                    })).mapping);
                                }),
                            "data-testid": Testids.deliveryRegistries.lcatMappingWithdraw,
                            children: "Withdraw mapping"
                        })
                    ]
                }),
                mapping.status === 'WITHDRAWN' && /*#__PURE__*/ _jsxs("section", {
                    "data-testid": Testids.deliveryRegistries.lcatMappingImpact,
                    children: [
                        /*#__PURE__*/ _jsx(SpecraHeading, {
                            children: "Withdrawal impact"
                        }),
                        /*#__PURE__*/ _jsxs(SpecraText, {
                            children: [
                                mapping.withdrawalReason,
                                " · ",
                                mapping.withdrawnBy,
                                " ·",
                                ' ',
                                mapping.withdrawnAt?.toDate().toLocaleString()
                            ]
                        }),
                        /*#__PURE__*/ _jsx(SpecraText, {
                            children: display(parseMappingJson(mapping.impactNoticeJson)?.message)
                        }),
                        /*#__PURE__*/ _jsx(SpecraText, {
                            children: "Original approval retained. Excluded from current use. Selections and rates remain unchanged."
                        }),
                        affectedStaffingReferences(mapping.impactNoticeJson).map((ref)=>/*#__PURE__*/ _jsxs(SpecraText, {
                                children: [
                                    "Staffing line ",
                                    ref.id,
                                    " · Program ",
                                    ref.program_id,
                                    " · Selected category ",
                                    ref.lcat_id
                                ]
                            }, ref.id))
                    ]
                }),
                /*#__PURE__*/ _jsx(SpecraButton, {
                    variant: "ghost",
                    onClick: onClose,
                    children: "Close review"
                })
            ]
        })
    });
}
